import React, { useState } from 'react';
import { Bell, ArrowUpRight, AtSign, Gavel, Handshake } from 'lucide-react';
import { GramIcon } from '@/components/ui/GramIcon';
import { UserTypeIcon, getUserKind, type UserKind } from '@/components/ui/UserTypeIcon';
import { useTranslation } from 'react-i18next';

const DARK_PALETTES = [
    { bg: '#174d29', textColor: '#ffffff' },
    { bg: '#1c1d22', textColor: '#fef08a' },
    { bg: '#0f243f', textColor: '#e0f2fe' },
    { bg: '#271138', textColor: '#fae8ff' },
    { bg: '#25282e', textColor: '#f1f5f9' },
];

// Nav Store / Cart SVG Icon (same as card)
const NavStoreIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 20" className={className} style={{ fill: 'currentColor' }}>
        <path d="M16.8035 6.96427C16.1335 6.22427 15.1235 5.79427 13.7235 5.64427V4.88427C13.7235 3.51427 13.1435 2.19427 12.1235 1.27427C11.6202 0.812823 11.025 0.462927 10.3771 0.247511C9.72909 0.032095 9.04292 -0.0439787 8.3635 0.0242742C5.9735 0.254274 3.9635 2.56427 3.9635 5.06427V5.64427C2.5635 5.79427 1.5535 6.22427 0.883496 6.96427C-0.0865043 8.04427 -0.0565042 9.48427 0.0534958 10.4843L0.753496 16.0543C0.963496 18.0043 1.7535 20.0043 6.0535 20.0043H11.6335C15.9335 20.0043 16.7235 18.0043 16.9335 16.0643L17.6335 10.4743C17.7435 9.48427 17.7635 8.04427 16.8035 6.96427ZM8.5035 1.41427C8.98813 1.36559 9.47758 1.41913 9.94023 1.57143C10.4029 1.72372 10.8284 1.97138 11.1894 2.29841C11.5503 2.62544 11.8387 3.02456 12.0357 3.46998C12.2328 3.91539 12.3343 4.39721 12.3335 4.88427V5.58427H5.3535V5.06427C5.3535 3.28427 6.8235 1.57427 8.5035 1.41427ZM5.2635 11.1543H5.2535C4.7035 11.1543 4.2535 10.7043 4.2535 10.1543C4.2535 9.60427 4.7035 9.15427 5.2535 9.15427C5.8135 9.15427 6.2635 9.60427 6.2635 10.1543C6.2635 10.7043 5.8135 11.1543 5.2635 11.1543ZM12.2635 11.1543H12.2535C11.7035 11.1543 11.2535 10.7043 11.2535 10.1543C11.2535 9.60427 11.7035 9.15427 12.2535 9.15427C12.8135 9.15427 13.2635 9.60427 13.2635 10.1543C13.2635 10.7043 12.8135 11.1543 12.2635 11.1543Z" />
    </svg>
);

export interface ListBarItemProps {
    displayName: string;
    username: string;
    avatarUrl?: string;
    price?: string | number;
    bg?: string;
    textColor?: string;
    grainOpacity?: number;
    sectionId?: string;
    itemType?: string;
    isAuction?: boolean;
    isSold?: boolean;
    isBrand?: boolean;
    isOffer?: boolean;
    userKind?: UserKind;
    onClick?: () => void;
    [key: string]: any;
}

export const ListCardBar: React.FC<ListBarItemProps> = (props) => {
    const {
        displayName,
        username,
        avatarUrl = '/icons/default-avatar.svg',
        price = '1,000',
        bg,
        textColor,
        grainOpacity = 0.30,
        sectionId,
        itemType,
        isAuction,
        isSold,
        isBrand,
        isOffer,
        userKind: userKindProp,
        onClick,
    } = props;

    const userKind = (userKindProp && userKindProp !== 'p2p') ? userKindProp : getUserKind(props);
    const { t } = useTranslation(['store', 'common']);
    const [isNotified, setIsNotified] = useState(false);

    let selected = DARK_PALETTES.find(p => p.bg.toLowerCase() === String(bg).toLowerCase());
    if (!selected) {
        let hash = 0;
        const key = username + displayName;
        for (let i = 0; i < key.length; i++) hash += key.charCodeAt(i);
        selected = DARK_PALETTES[Math.abs(hash) % DARK_PALETTES.length];
    }
    const pal = selected ?? DARK_PALETTES[1];
    const cardColor = textColor || pal.textColor;

    const stateBadge = isBrand
        ? { label: t('common:brands', 'Brand'), cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' }
        : isSold
            ? { label: t('common:sold', 'SOLD'), cls: 'bg-red-500/15 text-red-400 border-red-500/30' }
            : null;

    const handleArrowClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const cleanUser = String(username || '').replace(/^@/, '').trim();
        if (!cleanUser) return;
        const url = `https://t.me/${cleanUser}`;
        // @ts-ignore
        if (window.Telegram?.WebApp?.openTelegramLink) {
            // @ts-ignore
            window.Telegram.WebApp.openTelegramLink(url);
        } else {
            window.open(url, '_blank');
        }
    };

    const handleNotifClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsNotified(prev => !prev);
    };

    return (
        <div
            onClick={onClick}
            className="glass relative cursor-pointer select-none w-full overflow-hidden rounded-2xl border border-white/10 active:scale-[0.99] transition-transform"
            style={{ color: cardColor, ['--grain-opacity' as any]: grainOpacity }}
        >
            {/* Grain */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ opacity: 0.30, mixBlendMode: 'overlay', backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '4px 4px' }}
            />

            <div className="flex items-center gap-3 p-2.5 relative z-[2]">
                {/* Avatar */}
                <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 border border-white/25 glass-inset flex items-center justify-center">
                    <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                    />
                </div>

                {/* Meta */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                        <AtSign className="w-3 h-3 shrink-0" style={{ color: cardColor }} />
                        <span className="text-[15px] font-extrabold font-mono truncate" style={{ color: cardColor }}>
                            {username ? username.toUpperCase() : ''}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] opacity-60 min-w-0" style={{ color: cardColor }}>
                        <span className="truncate">{displayName}</span>
                        {stateBadge && (
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-md border font-mono font-bold shrink-0 ${stateBadge.cls}`}>
                                {stateBadge.label}
                            </span>
                        )}
                    </div>

                    <div className="mt-1 flex items-center gap-1.5">
                        {isOffer && !isAuction ? (
                            <span className="flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-purple-300">
                                <Handshake className="w-3.5 h-3.5" /> {t('makeOffer')}
                            </span>
                        ) : (
                            <>
                                <span className={`text-[13px] font-extrabold font-mono ${isAuction ? 'text-orange-400' : ''}`} style={!isAuction ? { color: cardColor } : undefined}>
                                    {price}
                                </span>
                                <GramIcon className={`w-3 h-3 ${isAuction ? 'text-orange-400' : ''}`} />
                            </>
                        )}
                    </div>
                </div>

                {/* 2x2 Action Icons Grid (2 Top, 2 Bottom) */}
                <div className="shrink-0 flex flex-col gap-1 z-10 items-end">
                    {/* Top row: Arrow (t.me/username) + Notification Bell (green toggle) */}
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={handleArrowClick}
                            aria-label="open-telegram"
                            title={username ? `https://t.me/${username.replace(/^@/, '')}` : t('openTelegram')}
                            className="flex items-center justify-center w-6 h-6 rounded-lg glass-chip hover:bg-white/20 transition-colors"
                        >
                            <ArrowUpRight className="w-3.5 h-3.5 text-white stroke-[2.2]" />
                        </button>
                        <button
                            type="button"
                            onClick={handleNotifClick}
                            aria-label="toggle-notifications"
                            title={isNotified ? t('notificationsActive') : t('enableNotifications')}
                            className={`relative flex items-center justify-center w-6 h-6 rounded-lg glass-chip transition-all ${
                                isNotified
                                    ? 'bg-emerald-500/20 border border-emerald-500/40'
                                    : 'hover:bg-white/20'
                            }`}
                        >
                            <Bell className={`w-3.5 h-3.5 stroke-[2.2] ${isNotified ? 'text-emerald-400' : 'text-white'}`} />
                            <span
                                className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full transition-colors ${
                                    isNotified
                                        ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse'
                                        : 'bg-red-500'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Bottom row: Cart (Store/Action) + 3rd Icon (UserType) */}
                    <div className="flex items-center gap-1">
                        <button type="button" aria-label="action" className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${isAuction ? 'bg-orange-500/20 border border-orange-400/30 hover:bg-orange-500/35' : 'glass-chip hover:bg-white/20'}`}>
                            {isOffer ? (
                                <Handshake className="w-3.5 h-3.5 text-purple-300" />
                            ) : isAuction ? (
                                <Gavel className="w-3.5 h-3.5 text-orange-400" />
                            ) : (
                                <NavStoreIcon className="w-3.5 h-3.5 text-white" />
                            )}
                        </button>
                        <button type="button" aria-label="user-type" className="glass-chip w-6 h-6 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors">
                            <UserTypeIcon kind={userKind} className="w-3.5 h-3.5 text-white" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListCardBar;